import _map from 'lodash/map'
import _merge from 'lodash/merge'
import _flatten from 'lodash/flatten'
import _uniq from 'lodash/uniq'
import _compact from 'lodash/compact'

import { randomString, runWithConcurrency } from "@/utils/Common";
import { db, Timestamp, FieldValue, getAllByIds } from "@/utils/Firebase";
import { categoryKey, routineObjectiveKey } from "@/utils/Taxonomy";
import store from '@/store';
import moment from 'moment';
import i18n from '@/plugins/i18n'

export const ASSIGN_CONCURRENCY = 8

export const SET_TYPE_ICONS = {
    reps: 'mdi-repeat',
    time: 'mdi-av-timer',
    minutes: 'mdi-clock-outline',
    distance: 'mdi-diameter-outline',
    weight: 'mdi-weight',
    rpe: 'mdi-chart-box-outline',
    km: 'mdi-speedometer',
    percentage: 'mdi-percent-outline',
    rir: 'mdi-cached',
    meter_seconds: 'mdi-diameter',
    tempo: 'mdi-timer-outline',
    additional: 'mdi-alarm-plus',
    intensity: 'mdi-chart-timeline-variant-shimmer',
    micropause: 'mdi-timer-off-outline',
    beat_min: 'mdi-heart-pulse',
    time_km: 'mdi-gauge',
    percentage_mas: 'mdi-run',
    percentage_mss: 'mdi-run-fast',
}

export const setTypeIcon = (shortName) => {
    if (!shortName) return null
    return SET_TYPE_ICONS[String(shortName).replace('short_', '')] || null
}

export const SET_NAMES = {
    REPS: ['short_reps', 'short_time', 'short_distance'],
    REPS_INTENSITY: ['short_reps', 'short_minutes', 'short_distance'],
    RPE: ['short_rpe', 'short_percentage', 'short_rir', 'short_meter_seconds'],
    TEMPO: ['short_tempo', 'short_additional'],
    INTENSITY: [
        'short_beat_min', 'short_time_km', 'short_km', 'short_rpe', 'short_time',
        'short_meter_seconds', 'short_percentage_mas', 'short_percentage_mss'
    ],
}

const setOption = (shortName) => {
    const value = String(shortName).replace('short_', '')
    return { name: i18n.t(`exercises.${value}`), value }
}

export const getRepsOptions = (hasIntensity = false) =>
    (hasIntensity ? SET_NAMES.REPS_INTENSITY : SET_NAMES.REPS).map(setOption)

export const getRpeOptions = () => SET_NAMES.RPE.map(setOption)

export const getTempoOptions = () => SET_NAMES.TEMPO.map(setOption)

export const getIntensityOptions = () => SET_NAMES.INTENSITY.map(setOption)

export const findSetData = (set, shortNames) =>
    (set?.data || []).find(data => shortNames.includes(data.short_name)) || null

export const setOptionByShortName = (options, shortName) =>
    (options || []).find(option => `short_${option.value}` === shortName) || null

const REPS_KEY_CHARS = '0123456789()maxdropy -:'

const eventChar = (event) => {
    if (typeof event.key === 'string' && event.key.length === 1) return event.key
    if (event.key) return null
    const code = event.charCode || event.keyCode
    return code ? String.fromCharCode(code) : null
}

const allowSetKey = (event, isAllowed) => {
    const char = eventChar(event)
    if (char === null) return true
    if (!isAllowed(char)) {
        event.preventDefault()
        return false
    }
    return true
}

const isDigit = (char) => char >= '0' && char <= '9'

const isLetter = (char) => (char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z')

export const isDecimalSetType = (type) => type?.value === 'meter_seconds' || type?.value === 'rpe'

export const validateRepsKey = (event) => allowSetKey(event, char => REPS_KEY_CHARS.includes(char.toLowerCase()))

export const validateNumberKey = (event, decimal = false) =>
    allowSetKey(event, char => isDigit(char) || (decimal && (char === '.' || char === ',')))

export const validateMinuteSecondsKey = (event) => allowSetKey(event, char => isDigit(char) || char === ':')

export const validateRpeKey = (event, type) => validateNumberKey(event, isDecimalSetType(type))

export const validateTempoKey = (event, type) => {
    const isTempo = type?.value === 'tempo'
    return allowSetKey(event, char => (isTempo ? isDigit(char) : isLetter(char)) || char === '.' || char === '-')
}

export const validateIntensityKey = (event, type) => {
    const value = type?.value || null
    if (value === 'time' || value === 'time_km') return validateMinuteSecondsKey(event)
    if (value === 'meter_seconds') return validateNumberKey(event, true)
    return allowSetKey(event, char => isDigit(char) || isLetter(char) || char === '/')
}

export const cleanNumberValue = (value, decimal = false) => {
    if (value === null || value === undefined || value === '') return value
    const clean = String(value).replace(/,/g, '.')
    if (!decimal) return clean.split('.')[0].replace(/[^\d]/g, '')
    const [first, ...rest] = clean.replace(/[^\d.]/g, '').split('.')
    return rest.length ? `${first}.${rest.join('')}` : first
}

export const cleanRpeValue = (value, type) => cleanNumberValue(value, isDecimalSetType(type))

export const cleanIntensityValue = (value, type) =>
    type?.value === 'meter_seconds' ? cleanNumberValue(value, true) : value

export const exerciseKey = (exercise) => exercise?.objectID || exercise?.id || null

export const getExercisesByBlocks = async (blocks) => {

    let allIds = _uniq(_compact(_flatten(_map(blocks, 'exercises')).map(exerciseKey)))
    if (allIds.length === 0) {
        return []
    }

    return await store.dispatch('fetchExercisesCache', allIds);
}

export const getMergedBlocks = async (blocks) => {

    blocks = blockUniqueTime(blocks)
    const exercises = await getExercisesByBlocks(blocks)
    let newBlocks = []

    blocks.forEach(block => {
        let newBlock = { ...block }
        newBlock.uid = block.uid || randomString(10, '', 'bl')
        let newExercises = []
        block.exercises.forEach(exercise => {
            let foundExercise = exercises.find(ex => exerciseKey(exercise) === ex?.objectID)
            let exerciseToAdd = { ...exercise, uid: exercise.uid || randomString(10, '', 'ex') }
            if (foundExercise) exerciseToAdd = _merge(exerciseToAdd, foundExercise)
            if (!exerciseToAdd.name) {
                console.warn(`[getMergedBlocks] ejercicio huerfano omitido, objectID=${exerciseKey(exercise)}`)
                return
            }
            newExercises.push(exerciseToAdd)
        })
        newBlock.exercises = newExercises
        newBlocks.push(newBlock)
    })
    return newBlocks
}

const getGroupAdvancesSeed = (studentsIds, newDocs, assignedRoutinesIds) => {
    const payload = { assignedRoutinesIds }
    studentsIds.forEach((studentId, index) => {
        const created = newDocs[index]
        if (created?.id) payload[`advances.${studentId}`] = { id: created.id }
    })
    return payload
}

export const addAssignedRoutinesGroup = async (assignedRoutine, studentsIds) => {
    let payload = null

    if (assignedRoutine.type === 'competence') {
        payload = getPayloadBaseAssignedCompetence(assignedRoutine.versus, assignedRoutine.minutes, assignedRoutine.date, assignedRoutine.coachId, assignedRoutine.routineNumber)
    } else {
        payload = getPayloadBaseAssignedRoutine(assignedRoutine.name, assignedRoutine.date, assignedRoutine, assignedRoutine.blocks, assignedRoutine.routineNumber || 1)
    }

    const ids = [...new Set((studentsIds || []).filter(Boolean))]
    const newDocs = await Promise.all(
        ids.map(id => db.collection('assignedRoutines').add({ ...payload, studentId: id, groupAssignedRoutineId: assignedRoutine.id }))
    )

    const assignedRoutinesIds = [...new Set([
        ...(assignedRoutine.assignedRoutinesIds || []),
        ...newDocs.map(d => d.id)
    ])]

    await db.collection('groupAssignedRoutines').doc(assignedRoutine.id).update(getGroupAdvancesSeed(ids, newDocs, assignedRoutinesIds))
}

export const updateAssignedRoutinesGroup = async (assignedRoutinesIds, blocks) => {
    await Promise.all((assignedRoutinesIds || []).map(id =>
        db.collection('assignedRoutines').doc(id).update({ blocks }).catch(() => {
            console.log('error', id)
        })
    ))
}

export const deleteAssignedRoutinesGroup = async (assignedRoutinesIds) => {
    await Promise.all((assignedRoutinesIds || []).map(id =>
        db.collection('assignedRoutines').doc(id).delete().catch(() => {
            console.log('error', id)
        })
    ))
}

export const hasAssignedRoutineInDate = async (studentId, date, routineNumber = 1) => {
    let assignedRoutinesRef = await db.collection('assignedRoutines')
        .where('studentId', '==', studentId)
        .where('rawDate.full', '==', date)
        .where('routineNumber', '==', routineNumber).get()
    return { has: assignedRoutinesRef.docs.length > 0, id: assignedRoutinesRef.docs?.[0]?.id || null }
}

export const hasGroupAssignedRoutineInDate = async (date, groupId, teamId = null, routineNumber = 1) => {
    let groupRoutinesRef = await db.collection('groupAssignedRoutines')
        .where('teamId', '==', teamId)
        .where('groupId', '==', groupId)
        .where('rawDate.full', '==', date)
        .where('routineNumber', '==', routineNumber).get()
    let payload = { has: groupRoutinesRef.docs.length > 0 }
    if (payload.has) {
        payload.id = groupRoutinesRef.docs[0].id
        payload.assignedRoutinesIds = groupRoutinesRef.docs[0].data().assignedRoutinesIds
    }
    return payload
}

export const getGroupAssignedRoutines = async (groupId, teamId = null) => {
    let now = moment().format('YYYY-MM-DD')
    let groupRoutinesRef = await db.collection('groupAssignedRoutines')
        .where('teamId', '==', teamId)
        .where('groupId', '==', groupId)
        .where('rawDate.full', '>=', now).get()
    return uniqueDayRoutine(groupRoutinesRef.docs.map(routine => ({
        id: routine.id,
        ...routine.data()
    })))
}

export const getAllGroupAssignedRoutines = async (groupId, teamId = null) => {
    let groupRoutinesRef = await db.collection('groupAssignedRoutines')
        .where('teamId', '==', teamId)
        .where('groupId', '==', groupId).get()
    return uniqueDayRoutine(groupRoutinesRef.docs.map(routine => ({
        id: routine.id,
        ...routine.data()
    })))
}

export const getAllStudentAssignedRoutines = async (studentId) => {
    let studentRoutinesRef = await db.collection('assignedRoutines')
        .where('studentId', '==', studentId).get()
    return uniqueDayRoutine(studentRoutinesRef.docs.map(routine => ({
        id: routine.id,
        ...routine.data()
    })))
}

export const deleteAssignedRoutineStudentInDate = async (studentId, date, routineNumber = 1) => {
    let assignedRoutinesRef = await db.collection('assignedRoutines')
        .where('studentId', '==', studentId)
        .where('rawDate.full', '==', date)
        .where('routineNumber', '==', routineNumber).get()
    await Promise.all(assignedRoutinesRef.docs.map(routine =>
        routine?.id ? db.collection('assignedRoutines').doc(routine.id).delete() : Promise.resolve()
    ))
}

export const deleteGroupAssignedRoutineInDate = async (groupId, teamId, date, routineNumber = 1) => {
    const snap = await db.collection('groupAssignedRoutines')
        .where('teamId', '==', teamId || null)
        .where('groupId', '==', groupId)
        .where('rawDate.full', '==', date)
        .where('routineNumber', '==', routineNumber).get()
    await Promise.all(snap.docs.map(async doc => {
        await deleteAssignedRoutinesGroup(doc.data().assignedRoutinesIds)
        await doc.ref.delete()
    }))
}

export const assignRoutineToSelection = async ({ routine, collection = 'assignedRoutines', selection = null, dates = [], routineNumber = 1 }) => {
    const currentSelection = (Array.isArray(selection) ? selection : [selection]).filter(Boolean)

    const tasks = []
    for (const date of dates) {
        for (const item of currentSelection) tasks.push({ date, item })
    }
    if (tasks.length === 0) return 0

    const blocks = clearBlocksAssignedRoutine(routine.blocks)

    await runWithConcurrency(tasks, ASSIGN_CONCURRENCY, async ({ date, item }) => {
        const routineName = `${item.name} - ${moment(date).format('DD-MM-YYYY')}`
        const payload = getPayloadBaseAssignedRoutine(routineName, date, routine, blocks, routineNumber)

        if (item.objectID) {
            payload.studentId = item.objectID
            await deleteAssignedRoutineStudentInDate(item.objectID, date, routineNumber)
        } else {
            payload.groupId = item.id
            payload.teamId = item.teamId || null
            await deleteGroupAssignedRoutineInDate(item.id, item.teamId, date, routineNumber)
        }

        const created = await db.collection(collection).add(payload)

        if (collection === 'groupAssignedRoutines') {
            await Promise.all((item?.students || []).map(student =>
                deleteAssignedRoutineStudentInDate(student, date, routineNumber)
            ))
            await addAssignedRoutinesGroup({ id: created.id, ...payload, date: date }, item.students)
        }
    })

    const count = tasks.length
    if (routine?.id) await db.collection('routines').doc(routine.id).update({ used: FieldValue.increment(count) })

    return count
}

export const programSessionDates = (startDate, weekdays = [], count = 0) => {
    const days = [...new Set(weekdays)].sort((a, b) => a - b)
    if (!startDate || !days.length || count <= 0) return []
    const dates = []
    const current = moment(startDate)
    let index = days.findIndex(day => current.day() <= day)
    if (index === -1) {
        current.add(1, 'week')
        index = 0
    }
    for (let i = 0; i < count; i++) {
        dates.push(current.day(days[index]).format('YYYY-MM-DD'))
        index++
        if (index >= days.length) {
            index = 0
            current.add(1, 'week')
        }
    }
    return dates
}

export const defaultProgramWeekdays = (startDate, daysPerWeek = 1) => {
    const start = moment(startDate).day()
    const count = Math.min(7, Math.max(1, Number(daysPerWeek) || 1))
    return Array.from({ length: count }, (_, i) => (start + Math.floor(i * 7 / count)) % 7)
}

export const assignProgramDates = async ({ program, coachId, collection, selections = [], dates = [], routineNumber = 1 }) => {
    const routines = program?.assignedRoutines || []
    const tasks = []
    for (const selection of selections) {
        dates.forEach((date, index) => tasks.push({ selection, date, index }))
    }

    await runWithConcurrency(tasks, ASSIGN_CONCURRENCY, async ({ selection, date, index }) => {
        const assignedRoutine = {
            ...routines[index],
            coachId,
            img: program?.img || '',
            imgSavePath: program?.imgSavePath || ''
        }
        const routineName = `${selection.name} - ${moment(date).format('DD-MM-YYYY')}`
        const payload = getPayloadBaseAssignedRoutine(routineName, date, assignedRoutine, assignedRoutine.blocks, routineNumber)

        if (selection.objectID) {
            payload.studentId = selection.objectID
            await deleteAssignedRoutineStudentInDate(selection.objectID, date, routineNumber)
        } else {
            payload.groupId = selection.id
            payload.teamId = selection.teamId || null
            await deleteGroupAssignedRoutineInDate(selection.id, selection.teamId, date, routineNumber)
        }

        const created = await db.collection(collection).add(payload)

        if (collection === 'groupAssignedRoutines') {
            await Promise.all((selection?.students || []).map(student =>
                deleteAssignedRoutineStudentInDate(student, date, routineNumber)
            ))
            await addAssignedRoutinesGroup({ id: created.id, ...payload, date }, selection?.students || [])
        }
    })

    if (tasks.length > 0 && program?.id) await db.collection('programs').doc(program.id).update({ used: FieldValue.increment(tasks.length) })
    return tasks.length
}

export const deleteAssignedRoutinesGroupCollection = async (groupId, teamId = null) => {
    let groupAssignedRoutinesRef = await db.collection('groupAssignedRoutines')
        .where('groupId', '==', groupId)
        .where('teamId', '==', teamId).get()
    await Promise.all(groupAssignedRoutinesRef.docs.map(routine =>
        routine?.id ? db.collection('groupAssignedRoutines').doc(routine.id).delete() : Promise.resolve()
    ))
}

export const removeStudentsFromGroup = async (studentsIds) => {
    await Promise.all((studentsIds || []).map(id =>
        db.collection('users').doc(id).update({ group: null, team: null })
    ))
}

export const setStudentGroup = async (studentId, newGroupId, oldGroupId, options = {}) => {
    if (!studentId) return
    if ((oldGroupId || null) === (newGroupId || null)) return
    const batch = db.batch()
    if (!options.skipUserUpdate) batch.update(db.collection('users').doc(studentId), { group: newGroupId || null })
    if (oldGroupId && oldGroupId !== newGroupId) {
        batch.update(db.collection('groups').doc(oldGroupId), {
            students: FieldValue.arrayRemove(studentId)
        })
    }
    if (newGroupId && newGroupId !== oldGroupId) {
        batch.update(db.collection('groups').doc(newGroupId), {
            students: FieldValue.arrayUnion(studentId)
        })
    }
    await batch.commit()
}

export const setStudentTeam = async (studentId, newTeam, oldTeam, options = {}) => {
    if (!studentId) return
    const sameTeam = oldTeam?.id && newTeam?.id && oldTeam.id === newTeam.id
    if (sameTeam && oldTeam.subgroupId === newTeam.subgroupId) return

    await db.runTransaction(async (tx) => {
        const userRef = db.collection('users').doc(studentId)

        const teamIds = [...new Set([oldTeam?.id, newTeam?.id].filter(Boolean))]
        const teamRefs = teamIds.map(id => db.collection('teams').doc(id))
        const teamSnaps = await Promise.all(teamRefs.map(ref => tx.get(ref)))
        const teamData = new Map()
        teamSnaps.forEach((snap, i) => {
            if (snap.exists) teamData.set(teamIds[i], JSON.parse(JSON.stringify(snap.data() || {})))
        })

        const removeFrom = (teamId, subgroupId) => {
            const data = teamData.get(teamId)
            if (!data) return
            const groups = data.groups || []
            const idx = groups.findIndex(g => g.id === subgroupId)
            if (idx < 0) return
            groups[idx].students = (groups[idx].students || []).filter(id => id !== studentId)
            data.groups = groups
        }
        const addTo = (teamId, subgroupId) => {
            const data = teamData.get(teamId)
            if (!data) return
            const groups = data.groups || []
            const idx = groups.findIndex(g => g.id === subgroupId)
            if (idx < 0) return
            const list = groups[idx].students || []
            if (!list.includes(studentId)) list.push(studentId)
            groups[idx].students = list
            data.groups = groups
        }

        if (oldTeam?.id) removeFrom(oldTeam.id, oldTeam.subgroupId)
        if (newTeam?.id) addTo(newTeam.id, newTeam.subgroupId)

        if (!options.skipUserUpdate) tx.update(userRef, { team: newTeam || null })
        for (const [id, data] of teamData.entries()) {
            tx.update(db.collection('teams').doc(id), { groups: data.groups || [] })
        }
    })
}

export const deleteGroupTeam = async (id, collection) => {
    await db.collection(collection).doc(id).delete()
}

export const clearBlocksAssignedRoutine = (blocks, reset = true) => {
    if (!blocks) return []
    return blockUniqueTime([...blocks]).map((block, i) => {
        const isCircuit = block.isCircuit || false
        const payload = {
            id: Number(i) + 1,
            sets: isCircuit ? block.sets || null : null,
            tabata: isCircuit ? block.tabata || null : null,
            isCircuit: isCircuit,
            type: isCircuit ? block.type || null : null,
            name: block.name || null,
            exercises: block.exercises.map(exercise => getPayloadExercise(exercise, reset))
        }
        if (!reset) {
            payload.completed = block.completed || false
            payload.duration = block.duration || 0
        }
        return payload
    })
}

export const clearBlock = (block, reset = true) => {
    if (!block) return null
    const isCircuit = block.isCircuit || false
    const payload = {
        sets: isCircuit ? block.sets || null : null,
        tabata: isCircuit ? block.tabata || null : null,
        isCircuit: isCircuit,
        type: isCircuit ? block.type || null : null,
        name: block.name || null,
        exercises: block.exercises.map(exercise => getPayloadExercise(exercise, reset))
    }
    if (!reset) {
        payload.completed = block.completed || false
        payload.duration = block.duration || 0
    }
    return payload
}

export const getPayloadBlock = (name, coachId, block, folder, tags = [], img = null, reset = true) => {
    return {
        name: name,
        block: clearBlock(block, reset),
        coachId: coachId,
        folder: folder.id,
        folderName: folder.name,
        tags: tags,
        img: img?.img || img?.tempUrl || '',
        imgSavePath: img?.imgSavePath || img?.savePath || '',
        created_at: Timestamp.now(),
    }
}

const cleanUndefinedDeep = (value) => {
    if (value === undefined) return null
    if (Array.isArray(value)) return value.map(cleanUndefinedDeep)
    if (value && typeof value === 'object') {
        const out = {}
        for (const key in value) out[key] = cleanUndefinedDeep(value[key])
        return out
    }
    return value
}

const stripDerivedSetFields = (config) => {
    if (!config) return config
    const { icon, ...rest } = config
    if (!rest.sets?.length) return rest
    return {
        ...rest,
        sets: rest.sets.map(set => ({
            ...set,
            data: (set?.data || []).map(({ icon: dataIcon, name, ...data }) => data),
        })),
    }
}

const sanitizeExerciseConfig = (config) => {
    if (!config) return null
    return cleanUndefinedDeep(stripDerivedSetFields(config))
}

export const getPayloadExercise = (exercise, reset = true) => {
    const payload = {
        type: exercise.type || 'exercise',
        time: exercise.time || '',
        name: exercise.name || null,
        maxWeight: exercise.maxWeight || null,
        eachSide: exercise.eachSide || false,
        commentary: exercise.commentary || '',
        hasTime: !!exercise.time || !!exercise.hasTime,
        id: exerciseKey(exercise),
        config: sanitizeExerciseConfig(exercise.config),
        painScale: exercise.painScale || false,
        weightMode: exercise.weightMode || false,
        prescription: exercise.prescription || null,
    }
    if (exercise.prescription || !reset) payload.currentWeek = exercise.currentWeek || 1
    if (!reset) {
        payload.completed = exercise.completed || false
        payload.commentaryFromStudent = exercise.commentaryFromStudent || ''
    }
    return payload
}

export const buildRoutineBlocksMeta = (blocks = []) => {
    const exercises = _flatten(_map(blocks, 'exercises'))
    const equipments = _uniq(_compact(_flatten(_map(exercises, 'equipments'))))
    const payloadBlocks = blocks.map((block, i) => {
        const isCircuit = block.isCircuit || false
        return {
            id: Number(i) + 1,
            name: block.name || null,
            sets: isCircuit ? block.sets || null : null,
            tabata: isCircuit ? block.tabata || null : null,
            isCircuit,
            type: isCircuit ? block.type || null : null,
            exercises: block.exercises?.map(ex => getPayloadExercise(ex)) || []
        }
    })
    return { equipments, blocks: payloadBlocks }
}

export const getBlockNextPrescription = (blocks, prescriptions) => {
    return blocks.map(block => {
        return {
            ...block,
            exercises: block.exercises.map(exercise => {
                if (exercise.prescription) {
                    let prescription = prescriptions.find(p => p.objectID === exercise.prescription)
                    if (prescription) {
                        let newConfig = prescription.config
                        let maxWeeks = prescription.weeks || 1
                        let newWeek = (exercise?.currentWeek || 1) + 1
                        if (maxWeeks >= newWeek) newConfig = prescription.weekConfigs?.[newWeek - 2] ?? newConfig
                        if (maxWeeks < newWeek) newWeek = 1
                        return {
                            ...exercise,
                            prescription: prescription.objectID,
                            currentWeek: newWeek,
                            config: newConfig
                        }
                    }
                }
                return exercise
            })
        }
    })
}

export const blockUniqueTime = (blocks) => {
    if (!blocks) return []
    let newBlocks = []
    blocks.forEach(block => {
        let exercises = []
        block.exercises.forEach((exercise, i) => {
            if (exercise.type !== 'rest') {
                let nextExercise = block.exercises[i + 1]
                if (nextExercise?.type === 'rest') {
                    exercise.time = nextExercise.time
                }
                exercises.push(exercise)
            }
        })

        block.exercises = exercises
        newBlocks.push(block)
    });
    return newBlocks
}

const PCT_NAMES = ['short_percentage', 'short_percentage_mas', 'short_percentage_mss']

export const routineLoadMetrics = (blocks = []) => {
    let series = 0, reps = 0, exercisesCount = 0
    const pctVals = []
    blocks.forEach(block => {
        const isCircuit = block.isCircuit
        ;(block.exercises || []).forEach(ex => {
            exercisesCount++
            const exCircuit = isCircuit || ex.isCircuit
            if (exCircuit) {
                const rounds = Number(block.sets) || 0
                series += rounds
                if ((ex.config?.type || 'reps') === 'reps') reps += rounds * (Number(ex.config?.value) || 0)
            } else {
                const sets = ex.config?.sets || []
                series += sets.length
                sets.forEach(set => {
                    const r = (set.data || []).find(d => d.short_name === 'short_reps')
                    if (r) reps += Number(r.value) || 0
                    const p = (set.data || []).find(d => PCT_NAMES.includes(d.short_name))
                    if (p && Number(p.value)) pctVals.push(Number(p.value))
                })
            }
        })
    })
    const intensity = pctVals.length ? pctVals.reduce((a, b) => a + b, 0) / pctVals.length : null
    return { series, reps, exercisesCount, intensity }
}

export const exerciseSetsLabel = (exercise, block, t) => {
    const isCircuit = block?.isCircuit || exercise?.isCircuit || false
    if (isCircuit && block?.type === 'tabata') return ''
    if (isCircuit) {
        const quantitySets = `${block?.sets || 0}x`
        const value = exercise?.config?.value || 1
        const type = i18n.t(`exercises.short_${exercise?.config?.type || 'reps'}`)
        return `${quantitySets}${value}${type}`
    }
    const sets = exercise?.config?.sets || []
    const quantitySets = sets.length
    if (quantitySets === 0) return i18n.t('common.not_available_short')
    const type = i18n.t(`exercises.${sets[0]?.data?.[0]?.short_name || 'short_reps'}`)
    if (quantitySets === 1) return `${quantitySets}x${sets[0]?.data?.[0]?.value}${type}`
    const values = []
    let current = null
    let sameValue = true
    sets.forEach(set => {
        const value = set?.data?.[0]?.value
        if (current === null) current = value
        values.push(value)
        if (current !== value) sameValue = false
        current = value
    })
    if (sameValue) return `${quantitySets}x${values[0]}${type}`
    return `${quantitySets}x${values.join(`${type},`)}${type}`
}

export const getPayloadBaseAssignedRoutine = (name, date, routine, blocks, routineNumber) => {
    const [year, month, day] = date.split('-');
    return {
        blocks: blocks,
        name: name,
        routineId: routine.routineId || null,
        date: Timestamp.fromMillis(moment(date).unix() * 1000),
        rawDate: {
            full: date,
            day: day,
            month: month,
            year: year
        },
        coachId: routine.coachId,
        img: routine.img || '',
        imgSavePath: routine.imgSavePath || '',
        description: routine.description || null,
        owner: routine.owner || 'user',
        routineNumber: routineNumber || 1,
    }
}

export const getPayloadBaseRoutine = (name, routine, blocks, category, img, tags = []) => {
    return {
        blocks: blocks,
        coachId: routine.coachId,
        created_at: Timestamp.now(),
        name: name,
        description: {
            blocks: blocks.length,
            equipments: routine?.description?.equipments || [],
            objective: category,
            objectiveKey: routineObjectiveKey(category) || ''
        },
        img: img?.tempUrl || '',
        imgSavePath: img?.savePath || '',
        tags: tags,
        owner: routine.owner || 'user',
    }
}

export const getPayloadBaseProgram = (name, coachId, assignedRoutines, category, description, img, tags = [], daysPerWeek = 1) => {
    return {
        coachId: coachId,
        created_at: Timestamp.now(),
        name: name,
        category: category,
        categoryKey: categoryKey(category) || '',
        img: img?.tempUrl || '',
        imgSavePath: img?.savePath || '',
        assignedRoutines: assignedRoutines,
        description: description,
        tags: tags,
        daysPerWeek: daysPerWeek,
        owner: 'user'
    }
}

export const getPayloadBaseAssignedCompetence = (versus, minutes = 0, date, coachId, routineNumber) => {
    const [year, month, day] = date.split('-');
    return {
        versus: versus,
        minutes: minutes,
        date: Timestamp.fromMillis(moment(date).unix() * 1000),
        rawDate: {
            full: date,
            day: day,
            month: month,
            year: year
        },
        coachId: coachId,
        owner: 'user',
        type: 'competence',
        routineNumber: routineNumber || 1,
    }
}

export const addAssignedCompetenceGroup = async (competence, studentsIds) => {
    let payload = getPayloadBaseAssignedCompetence(competence.versus, competence.minutes, competence.date, competence.coachId, competence.routineNumber)

    const ids = [...new Set((studentsIds || []).filter(Boolean))]
    const newDocs = await Promise.all(
        ids.map(id => db.collection('assignedRoutines').add({ ...payload, studentId: id, groupAssignedRoutineId: competence.id }))
    )

    const assignedRoutinesIds = [...new Set([
        ...(competence.assignedRoutinesIds || []),
        ...newDocs.map(d => d.id)
    ])]

    await db.collection('groupAssignedRoutines').doc(competence.id).update(getGroupAdvancesSeed(ids, newDocs, assignedRoutinesIds))
}

export const updateAssignedCompetenceGroup = async (assignedRoutinesIds, versus, minutes) => {
    await Promise.all((assignedRoutinesIds || []).map(id =>
        db.collection('assignedRoutines').doc(id).update({ versus, minutes }).catch(() => {
            console.log('error', id)
        })
    ))
}

export const uniqueDayRoutine = (routines) => {
    const filteredRoutines = routines.reduce((acc, routine) => {
        const day = routine?.rawDate?.full || null;
        const studentId = routine.studentId;
        const routineNumber = routine.routineNumber || 1;

        if (!acc[day]) acc[day] = {};
        if (!acc[day][studentId]) acc[day][studentId] = {};

        if (!acc[day][studentId][routineNumber]) {
            acc[day][studentId][routineNumber] = routine;
        }

        return acc;
    }, {});

    const uniqueRoutines = Object.values(filteredRoutines).flatMap(dayRoutines =>
        Object.values(dayRoutines).flatMap(studentRoutines =>
            Object.values(studentRoutines)
        )
    );

    return uniqueRoutines;
};

// fetchAssigned: cuando hay alumno seleccionado se traen TODAS las assignedRoutines
// del equipo. Es necesario aunque solo se mire un alumno, porque la serie EQUIPO
// de los charts (AcuteChronic / Variables) es el PROMEDIO del equipo y se calcula
// a partir del advance de todos los alumnos (ver `processed` más abajo y
// `loadStats`/`getStudentDocs` en los charts). No se puede acotar a un alumno.
export const uniqueDayTeamRoutine = async (teamRoutines = [], students = [], fetchAssigned = true, advancesById = null) => {
    let currentTeamRoutines = [...teamRoutines];

    const docsData = currentTeamRoutines.map(r => r.data());

    const routineMap = new Map();
    if (fetchAssigned) {
        if (advancesById) {
            advancesById.forEach((v, id) => routineMap.set(id, v));
        } else {
            const uniqueIds = [...new Set(
                docsData.flatMap(d => d.assignedRoutinesIds || []).filter(id => typeof id === 'string' && id.length > 0)
            )];
            if (uniqueIds.length > 0) {
                const fetchedRoutines = await getAllByIds('assignedRoutines', uniqueIds);
                fetchedRoutines.forEach(r => { if (r?.id) routineMap.set(r.id, r); });
            }
        }
    }

    await getMergedBlocks(docsData.flatMap(d => d.blocks || []));

    const loadedRoutines = await Promise.all(
        currentTeamRoutines.map(async (currentRoutine) => {
            const data = currentRoutine.data();
            const routineIds = data.assignedRoutinesIds || [];

            const assignedRoutines = routineIds
                .map(routineId => {
                    const routineData = routineMap.get(routineId);
                    if (!routineData) return null;
                    return {
                        assignedRoutinesId: routineId,
                        advance: routineData.advance || null,
                        student: students.find(student => student.objectID === routineData.studentId) || null,
                    };
                })
                .filter(Boolean);

            const blocks = await getMergedBlocks(data.blocks || []);
            return {
                ...data,
                id: currentRoutine.id,
                blocks,
                assignedRoutines,
            };
        })
    );

    const grouped = loadedRoutines.reduce((acc, curr) => {
        const date = curr.rawDate?.full;
        if (!date) return acc;

        if (acc[date]) {
            acc[date].blocks = [...acc[date].blocks, ...curr.blocks];
            acc[date].assignedRoutinesIds = [...acc[date].assignedRoutinesIds || [], ...curr.assignedRoutinesIds || []];
            acc[date].assignedRoutines = [
                ...acc[date].assignedRoutines,
                ...curr.assignedRoutines,
            ];
        } else {
            acc[date] = { ...curr };
        }

        return acc;
    }, {});

    const processed = Object.values(grouped).map((doc) => {
        let preSum = {};
        let preCount = 0;
        let postSum = {};
        let postCount = 0;

        doc.assignedRoutines.forEach((routine) => {
            const adv = routine.advance;
            if (!adv) return;

            if (adv.pre && adv.preInfo) {
                for (const [key, val] of Object.entries(adv.preInfo)) {
                    if (typeof val === 'number') {
                        preSum[key] = (preSum[key] || 0) + val;
                    }
                }
                preCount++;
            }

            if (adv.post && adv.postInfo) {
                for (const [key, val] of Object.entries(adv.postInfo)) {
                    if (key === 'comment') continue;
                    postSum[key] = (postSum[key] || 0) + Number(val || 0);
                }
                postCount++;
            }
        });

        const preAverage = {};
        const postAverage = {};

        if (preCount > 0) {
            for (const key in preSum) {
                preAverage[key] = +(preSum[key] / preCount).toFixed(1);
            }
        }

        if (postCount > 0) {
            for (const key in postSum) {
                postAverage[key] = +(postSum[key] / postCount).toFixed(1);
            }
        }

        return {
            ...doc,
            advance: {
                pre: preCount > 0,
                preInfo: preCount > 0 ? preAverage : null,
                post: postCount > 0,
                postInfo: postCount > 0 ? postAverage : null,
            },
        };
    });

    return processed.sort((a, b) => a.rawDate.full.localeCompare(b.rawDate.full));
}